import { useQueryClient } from '@tanstack/react-query'
import to from 'await-to-js'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { Control, FieldValues, Path, UseFormSetError } from 'react-hook-form'
import apiService from 'services/api'
import { handleFormError } from 'utils/handleFormError'
import { toast } from 'react-toastify'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'
import { Tag } from 'models/tags'

export function CreatableTagSelector<T extends FieldValues>({
  name,
  control,
  options,
  setError,
  label,
  placeholder,
  tagParent = 'products',
  className,
  optional,
  labelClassName,
  whiteBackground = false,
}: {
  name: Path<T>

  control: Control<T, any>

  options: any
  setError: UseFormSetError<T>
  label?: string
  placeholder?: string
  tagParent?: 'products' | 'contacts' | 'documents'
  className?: string
  optional?: boolean
  labelClassName?: string
  whiteBackground?: boolean
}) {
  const api = apiService()
  const queryClient = useQueryClient()
  const { data: tags } = api.useGetTags(tagParent)

  const onCreateOption = async (value: string) => {
    if (value.length > 50) {
      toast.error('Tag length limited to 50 characters.')
      return null
    }

    // For documents, tags are created automatically during upload
    // So we just return the string value for selection
    if (tagParent === 'documents') {
      return {
        label: value,
        value: value, // Use string value for documents
      }
    }

    const [err, res] = await to(
      api.postTag(
        {
          tag: value,
        },
        tagParent
      )
    )

    if (err) handleFormError(err, setError)
    else {
      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: ['tags', tagParent] })
      return {
        label: res?.tag,
        value: res?.id,
      }
    }

    return null
  }

  const getTagColor = (tagValue: any) => {
    // For documents, tagValue is a string (tag name)
    // For contacts/products, tagValue is a number (tag ID)
    const tag = tags?.find((t: Tag) =>
      tagParent === 'documents' ? t.tag === tagValue : t.id === tagValue
    )
    if (tag?.color) {
      return (
        MULTI_VALUE_SELECT_COLORS.find((c) => c.background === tag.color) ||
        MULTI_VALUE_SELECT_COLORS[0]
      )
    }
    return MULTI_VALUE_SELECT_COLORS[0]
  }

  const customStyles = {
    ...(whiteBackground && {
      control: (provided: any) => ({
        ...provided,
        backgroundColor: 'white',
      }),
    }),
    multiValue: (provided: any, state: any) => {
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        backgroundColor: color.background,
        color: color.text,
      }
    },
    multiValueLabel: (provided: any, state: any) => {
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        color: color.text,
      }
    },
    multiValueRemove: (provided: any, state: any) => {
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        color: color.text,
        ':hover': {
          backgroundColor: color.background,
          color: color.text,
        },
      }
    },
    option: (provided: any, state: any) => {
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        ':before': {
          content: '""',
          display: 'inline-block',
          width: '12px',
          height: '12px',
          borderRadius: '3px',
          backgroundColor: color.background,
          flexShrink: 0,
        },
      }
    },
  }

  return (
    <CreatableSelect
      name={name}
      isMulti
      control={control}
      type="text"
      label={label}
      placeholder={placeholder}
      options={options}
      onCreateOption={onCreateOption}
      className={className}
      optional={optional}
      styles={customStyles}
      labelClassName={labelClassName}
    />
  )
}
